import { useEffect, useMemo } from 'react';
import { type TourElements, useTourContext } from '@/store/tourContext';

export interface UseAppTourOptions {
	elements: TourElements;
}

/**
 * Hook to register tour elements with the global tour context.
 * This hook should be used in components that have elements to be included in the tour.
 * The actual tour logic is handled by the TourProvider.
 * To start or stop the tour, use the helpers returned by `useTourContext()`.
 */
export function useAppTour({ elements }: UseAppTourOptions): void {
	const { registerElement } = useTourContext();

	const entries = useMemo(
		() =>
			Object.entries(elements).filter(
				(
					value,
				): value is [keyof TourElements, HTMLElement | null | undefined] =>
					value[1] !== undefined,
			),
		[elements],
	);

	useEffect(() => {
		entries.forEach(([key, element]) => {
			registerElement(key, element ?? null);
		});

		return () => {
			entries.forEach(([key]) => {
				registerElement(key, null);
			});
		};
	}, [entries, registerElement]);
}
